import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { getObjectCodeUrl } from '@pages/ConfiguredEntity/helpers/getObjectCodeUrl'

import { getDefaultValuesByObjectFields } from '@components/DisplayForm/helpers'
import { useCurrentRow } from '@components/DisplayForm/hooks/useCurrentRow'
import { useFetchObjectDataWithFixedKey } from '@components/DisplayForm/hooks/useFetchObjectDataWithFixedKey'
import { useHandleNavigation } from '@components/DisplayForm/hooks/useHandleNavigation'
import { useLatestFormVariables } from '@components/DisplayForm/hooks/useLatestFormVariables'
import { usePreFillConfig } from '@components/DisplayForm/hooks/usePreFillConfig'
import { useSetFormValueOnVariableChange } from '@components/DisplayForm/hooks/useSetFormValueOnVariableChange'
import { useVerifyFormCode } from '@components/DisplayForm/hooks/useVerifyFormCode'

import { useFetchObjectByCodeQuery, useFetchViewByCodeQuery } from '@redux/api'

import {
  useAppSelector,
  useChangeVariableActions,
  usePkField,
  usePrompt,
  useSubscription,
} from '@hooks'
import { getEntityTypeForRequest, ReturnTransformCommonParametersToValues } from '@helpers'
import { ENTITY, ENTITY_TYPES_REQUEST, FORM_TYPE, OuterDataType, PreFillType } from '@constants'
import {
  FormContainerEnriched,
  FormType,
  LinkFormHistory,
  LinkType,
  LinkViewHistory,
  ObjectDataRecord,
  PassedFormData,
} from '@types'

import { Row } from '../types'

import { useFetchRowByUpdate } from './useFetchRowByUpdate'
import { useHandlers } from './useHandlers'
import { useSetBlockingUIOverlay } from './useSetBlockingUIOverlay'
import { useOnInitOpenForm } from '@components/DisplayForm/hooks/useOnInitOpenForm'

type useDisplayFormParams = {
  passedParams: PassedFormData
  onChangeDirty?: (value: boolean) => void
  onBreadcrumbsMainLinkClick: (link?: LinkViewHistory) => void
  onObjectDataRecordSuccessCreating?: (links: LinkType[], data: ObjectDataRecord) => void
  handleNavigateToDefaultPage: () => void
  handleChangeFormCode: (formCode: string, verifyFormCode: string) => void
  handleRemoveSamePageForm: (formDataId?: number, isDirty?: boolean) => void
  handleUpdateSamePageFormData: (
    oldObjectId: string,
    newFormCode: string,
    objectCode: string,
    objectId: string
  ) => void
  handleCloseCurrentFormTab: () => void
  entityType: ENTITY
  dialogId?: string
  preFill?: PreFillType
  outerData?: OuterDataType
  resetFieldsAfterCreate?: boolean
  isSectionWindow?: boolean
  isIframeForm?: boolean
  formConfig: FormType
  formContainersConfig: FormContainerEnriched[]
  initialCommonParameters?: ReturnTransformCommonParametersToValues
  instanceId: string
}

export const useDisplayForm = ({
  passedParams,
  onChangeDirty,
  onBreadcrumbsMainLinkClick,
  onObjectDataRecordSuccessCreating,
  handleNavigateToDefaultPage,
  handleChangeFormCode,
  handleRemoveSamePageForm,
  handleUpdateSamePageFormData,
  handleCloseCurrentFormTab,
  entityType,
  dialogId,
  preFill: preFillFromAnotherObject,
  outerData,
  resetFieldsAfterCreate,
  isSectionWindow,
  isIframeForm,
  formConfig,
  initialCommonParameters,
  instanceId,
}: useDisplayFormParams) => {
  const { formCode, objectId, viewCode, path, globalId, viewId, typeId } = passedParams
  const withPassedParams = Boolean(passedParams?.id)

  const { objectsWithDirtyRecords } = useAppSelector(state => state.changedFormFields)

  const viewType = getEntityTypeForRequest(entityType)

  const { data: view, isSuccess: isSuccessView } = useFetchViewByCodeQuery(
    { code: viewCode, viewType },
    {
      skip: !viewCode || viewType === ENTITY_TYPES_REQUEST.GANTT_CHART,
    }
  )

  const outerVariables = outerData?.variables
  const formHistory = useAppSelector(state => state.formHistory)[globalId] || {}

  const { links, selectedIndex } = formHistory
  const selectedLink = selectedIndex ? links[selectedIndex] : null
  const currentLink: LinkFormHistory = useMemo(
    () =>
      selectedLink && 'formCode' in selectedLink
        ? selectedLink
        : {
            event: passedParams.event,
            formCode,
            id: globalId,
            isWindow: false,
            objectCode: formConfig?.objectCode || '',
            objectId: objectId || null,
            title: formCode,
            typeId,
          },
    [formCode, globalId, formConfig?.objectCode, objectId, passedParams, selectedLink, typeId]
  )

  const formCodeToFetchBy = currentLink.formCode

  const { data: objectModel } = useFetchObjectByCodeQuery(formConfig?.objectCode, {
    skip: !formConfig?.objectCode,
  })

  const pkField = usePkField(objectModel?.fields)

  const viewPath = path ?? ''

  const [_, { data: rowData, isLoading: isLoadingRow }] = useFetchObjectDataWithFixedKey(
    globalId,
    typeId,
    formConfig?.fields
  )

  const methods = useForm<Row>({
    defaultValues: getDefaultValuesByObjectFields(
      formConfig?.fields || [],
      rowData && currentLink.event !== FORM_TYPE.CREATE ? rowData : {}
    ),
  })
  const {
    setValue: setValueOnForm,
    reset,
    watch,
    formState: { isDirty, dirtyFields, isValid, errors },
    getValues,
  } = methods

  const preFillConfig = usePreFillConfig({
    formConfig,
    preFillFromAnotherObject,
  })

  const { latestVariables } = useLatestFormVariables({ instanceId })

  const { data, state, handlers } = useHandlers({
    passedParams,
    formConfig,
    methods,
    withPassedParams,
    currentLink,
    dialogId,
    onBreadcrumbsMainLinkClick,
    onObjectDataRecordSuccessCreating,
    handleRemoveSamePageForm,
    handleUpdateSamePageFormData,
    handleCloseCurrentFormTab,
    pkField,
    formCodeToFetchBy,
    outerData,
    resetFieldsAfterCreate,
    isIframeForm,
    preFillConfig,
    instanceId,
    latestVariables,
    objectModel,
  })

  const {
    handleObjectSubscribe,
    handleSetInitialFieldsValuesForCreateForm,
    handleResetFormWithNewRowData,
  } = handlers
  const { hasUnsavedChanges } = state
  const { isViewType, row, preFillFetchedRow } = data

  usePrompt({ when: isDirty })

  useSubscription(getObjectCodeUrl(currentLink.objectCode), handleObjectSubscribe)

  useChangeVariableActions({
    objectData: row,
    preFillObjectData: preFillFetchedRow,
    actions: formConfig?.actions,
    outerVariables,
    uniqueEventId: instanceId,
  })

  useSetFormValueOnVariableChange({
    formActions: formConfig?.actions,
    setValue: setValueOnForm,
    isViewType,
    instanceId,
    row,
    formVariables: latestVariables,
    outerVariables,
    watch,
    dialogId,
    getValues,
  })

  useFetchRowByUpdate({
    objectCode: currentLink.objectCode,
    objectId: currentLink.objectId || undefined,
    objectsWithDirtyRecords,
    globalId,
    typeId,
    fields: formConfig.fields || [],
    overrideCommands: formConfig.commands,
    objectCommands: objectModel?.commands,
    handleResetFormWithNewRowData,
  })

  useVerifyFormCode({
    newEvent: currentLink.event,
    view,
    isSuccessView,
    withPassedParams,
    formCode,
    newFormCode: currentLink.formCode,
    handleChangeFormCode,
    globalId,
    typeId,
    fields: formConfig.fields || [],
  })

  const { setCurrentRow } = useCurrentRow(globalId)

  useHandleNavigation({
    handleNavigateToDefaultPage,
    setCurrentRow,
    withPassedParams,
    newEvent: currentLink.event,
    globalId,
    typeId,
    fields: formConfig.fields || [],
  })

  useSetBlockingUIOverlay({
    form: formConfig,
    dialogId,
    formEvent: currentLink.event,
    isSectionWindow,
  })

  useOnInitOpenForm({
    actions: formConfig?.actions,
    dialogId,
    objectData: row,
    preFillObjectData: preFillFetchedRow,
    outerVariables,
    formVariables: latestVariables,
  })

  useEffect(() => {
    handleSetInitialFieldsValuesForCreateForm()
  }, [])

  useEffect(() => {
    if (onChangeDirty) {
      onChangeDirty(isDirty)
    }
  }, [isDirty, onChangeDirty])

  const computedIsFetchingForm = state.isPendingCreateOrUpdateForm

  return {
    data: {
      ...data,
      form: formConfig,
      viewCode,
      // TODO isLoadingForm
      isFetchingForm: computedIsFetchingForm,
      isFetchingFormError: false,
      view,
      withPassedParams,
      viewPath,
      isDirty: isDirty || hasUnsavedChanges,
      methods,
      dirtyFields,
      formCodeToFetchBy,
      outerVariables,
      isValid: isValid && !Object.keys(errors || {}).length,
    },
    handlers: {
      ...handlers,
      reset,
      setValueOnForm,
      watch,
    },
    state: {
      ...state,
      currentRow: row,
      currentLink,
      initialCommonParameters,
      isLoadingRow,
      latestVariables,
    },
  }
}
